import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import cn from 'classnames';

import { BaseButton, IconButton } from '../../BaseComponents';
import { useTheme } from '../../theme';
import { PromoCodeFieldType, PromoCodeFormProps, PromoCodeFormState, PromoCodeStatus } from '../types';
import { isExistI18nTranslate } from '../../utils';
import { useConfig } from '../../context';
import { CdnIcon } from '@shared/ui';
import FormField from './Field/Field';
import Input from '../../Input';

const PromoCodeForm: React.FC<PromoCodeFormProps> = props => {
	const { t } = useTranslation('PromoCode');
	const { PromoCodeForm: theme } = useTheme('PromoCode');
	const { promoCodesWithAgreement = [] } = useConfig()?.SearchForm;

	const title = isExistI18nTranslate('Title', { ns: 'PromoCode' }) ? t('Title') : props.title;

	const getPromoCodeAgreement = useCallback(
		(promoCode: string) => {
			if (!promoCodesWithAgreement.length) {
				return undefined;
			}

			return promoCodesWithAgreement?.find(promo => promo.regex.test(promoCode));
		},
		[promoCodesWithAgreement]
	);

	const validateRequired = useCallback(
		(value: string) => {
			const isRequired = typeof value === 'boolean' ? !value : !value || !value.trim?.();

			return isRequired ? t('Required') : undefined;
		},
		[t]
	);

	return (
		<>
			{title && <div className={theme.title}>{title}</div>}
			<Form<PromoCodeFormState> onSubmit={props.onSubmit}>
				{({ handleSubmit, values }) => {
					const promoCodeAgreement = getPromoCodeAgreement(values.promoCode);

					return (
						<form onSubmit={handleSubmit} className={theme.form}>
							<Field name="promoCode" validate={validateRequired}>
								{({ input, meta }) => (
									<Input
										className={theme.codeInput}
										absoluteHintPosition={true}
										TextFieldProps={{
											...input,
											label: t('Enter a promo code'),
											type: 'input',
											error: meta.touched && !meta.valid,
											helperText: meta.touched && !meta.valid ? meta.error : null
										}}
									/>
								)}
							</Field>

							{promoCodeAgreement && (
								<div className={theme.form_additionalFields}>
									<FormField
										name="agreement"
										type={PromoCodeFieldType.Checkbox}
										label={t(`promoCheckboxLabel_${promoCodeAgreement.code}`)}
										validate={validateRequired}
									/>
								</div>
							)}

							<div
								className={cn(theme.statusMessage, {
									[theme.statusMessage_applied]: props.status === PromoCodeStatus.Applied,
									[theme.statusMessage_notApplied]: props.status === PromoCodeStatus.NotApplied
								})}
							>
								{props.submitButtonText && (
									<BaseButton
										type={'submit'}
										className={cn(theme.button, {
											// [theme.button_empty]: props.status === PromoCodeStatus.Empty,
											// [theme.button_applied]: props.status === PromoCodeStatus.Applied
										})}
										isLoading={props.isLoading}
										data-promocode-submit-button={true}
									>
										{(props.status === PromoCodeStatus.Empty ||
											props.status === PromoCodeStatus.NotApplied) &&
											props.submitButtonText}
									</BaseButton>
								)}
								{!props.submitButtonText && (
									<IconButton
										type={'submit'}
										className={cn(theme.button, {
											[theme.button_empty]: props.status === PromoCodeStatus.Empty,
											[theme.button_applied]: props.status === PromoCodeStatus.Applied
										})}
										isLoading={props.isLoading}
										isSuccess={props.status === PromoCodeStatus.Applied}
										data-promocode-submit-button={true}
									>
										{(props.status === PromoCodeStatus.Empty ||
											props.status === PromoCodeStatus.NotApplied) && (
											<CdnIcon icon="Arrow/Forward" />
										)}
									</IconButton>
								)}
								{!props.isLoading && (
									<span className={theme.statusText}>
										{props.status === PromoCodeStatus.Applied && t('Promo code applied')}
										{/*{props.status === PromoCodeStatus.NotApplied && t('Promo code not applied for this flights')}*/}
									</span>
								)}
							</div>
						</form>
					);
				}}
			</Form>
		</>
	);
};

export default PromoCodeForm;
